import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Grid, Button, Alert, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { createTeam } from '../../services/teamService';

const CURRENT_YEAR = new Date().getFullYear();

function CreateTeamInner() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    teamName: '', organizationName: '', city: '', state: '', zipCode: '',
    season: String(CURRENT_YEAR), gradeAgeGroup: '', alsoHeadCoach: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.teamName || !form.city || !form.state || !form.zipCode || !form.season || !form.gradeAgeGroup) {
      setError('Team name, city, state, ZIP, season, and grade/age group are required.');
      return;
    }
    setSaving(true);
    try {
      const team = await createTeam({
        teamName: form.teamName,
        organizationName: form.organizationName,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        season: Number(form.season),
        gradeAgeGroup: form.gradeAgeGroup,
        alsoHeadCoach: form.alsoHeadCoach,
      });
      navigate(`/my-teams/${team.id}`);
    } catch (e2) {
      setError(e2.message || 'Could not create team.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>Create Team</Typography>
      <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
        You'll become this team's Team Admin.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField fullWidth label="Team name" value={form.teamName} onChange={set('teamName')} required placeholder="Jefferson Eagles" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Organization / School" value={form.organizationName} onChange={set('organizationName')} placeholder="Optional — e.g. Jefferson Youth Football" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="City" value={form.city} onChange={set('city')} required />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label="State" value={form.state} onChange={set('state')} required inputProps={{ maxLength: 2 }} placeholder="MI" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label="ZIP code" value={form.zipCode} onChange={set('zipCode')} required placeholder="49247" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth type="number" label="Season" value={form.season} onChange={set('season')} required
              inputProps={{ min: 2000, max: 2100, step: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Grade / age group" value={form.gradeAgeGroup} onChange={set('gradeAgeGroup')} required placeholder="6th Grade" />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={<Checkbox checked disabled />}
            label="Team Admin (that's you, automatically)"
          />
          <FormControlLabel
            control={<Checkbox checked={form.alsoHeadCoach} onChange={e => setForm(f => ({ ...f, alsoHeadCoach: e.target.checked }))} />}
            label="I am also the Head Coach"
          />
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Button
          type="submit" variant="contained" color="primary" disabled={saving} size="large"
          sx={{ mt: 3, textTransform: 'none', fontWeight: 800, px: 4 }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#050c16' }} /> : 'Create Team'}
        </Button>
      </Box>
    </Container>
  );
}

export default function CreateTeamPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><CreateTeamInner /></RequireAuth>
    </Box>
  );
}
